// A aba de negócios da ficha 360: as oportunidades do cliente. (#880)

import { type ReactNode } from 'react';
import type { Cliente360, OportunidadeDo360 } from '@cliqcrm/contracts';
import { dinheiro } from '@/shared/lib/formato';
import { Defasado, Vazio } from '@/shared/ui/estados/estados';
import { nomeDaEtapa } from '@/entities/comercial/etapa';
import { Chip, ListaLinha, Tabela } from '@/shared/ui';

/**
 * Os três painéis de lista: tabela no mouse, `lista-linha` no dedo.
 *
 * ── Por que DUAS peças, e não uma tabela encolhida ────────────────────────
 *
 * As tabelas têm quatro e cinco colunas. Em 390px elas precisariam de ~700px e
 * rolariam de lado — com a primeira coluna, que é o assunto da linha, saindo da
 * vista junto. O corte é 1024 e não 720: com o menu aberto, 1024px de janela
 * deixam ~720px de conteúdo.
 */
export function PainelDeNegocios({
  lista,
  aoAbrir,
}: {
  lista: Cliente360['oportunidades'];
  /** Abre o card grande da oportunidade (28/09/2026). */
  aoAbrir: (oportunidadeId: string) => void;
}): ReactNode {
  if (lista.itens.length === 0) {
    return (
      <Vazio
        titulo="Nenhuma oportunidade ainda"
        porque="Criar uma é o que faz este cliente entrar no funil."
      />
    );
  }

  const previsao = (o: OportunidadeDo360): string =>
    o.fechamento_previsto_em
      ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(
          new Date(o.fechamento_previsto_em),
        )
      : '—';

  return (
    <>
      {lista.frescor.stale && (
        <Defasado projetadoEm={lista.frescor.projetado_em} origem="Esta lista" />
      )}

      <div className="c360__so-mouse">
        <Tabela
          rotulo="Oportunidades deste cliente"
          colunas={[
            // "Oportunidade", e não "Veículo" (28/09/2026): o título do
            // negócio nem sempre é um carro, e o produto atende outros ramos.
            { chave: 'titulo', rotulo: 'Oportunidade' },
            { chave: 'etapa', rotulo: 'Etapa' },
            { chave: 'valor', rotulo: 'Valor', numero: true },
            { chave: 'previsao', rotulo: 'Previsão' },
          ]}
          chaveDe={(_, i) => lista.itens[i]!.referencia_externa}
          linhas={lista.itens.map((o) => ({
            titulo: (
              <>
                {/*
                  Só a que tem id canônico abre: a criada fora do CliqCRM não
                  tem por onde ser buscada, e o recado ao lado já diz isso.
                */}
                {o.id ? (
                  <button type="button" className="c360__abrir" onClick={() => aoAbrir(o.id!)}>
                    {o.titulo ?? 'Sem título'}
                  </button>
                ) : (
                  (o.titulo ?? 'Sem título')
                )}
                {/* `id` nulo = a engine conhece e o canônico ainda não foi
                    cunhado (C13). Dizer isso evita procurar uma ação que não
                    existe. */}
                {!o.id && <span className="c360__so-engine"> · criada fora do CliqCRM</span>}
              </>
            ),
            etapa: o.etapa ? <Chip tom="etapa">{nomeDaEtapa(o.etapa_nome ?? o.etapa)}</Chip> : '—',
            valor: <span className="c360__valor">{dinheiro(o.valor) ?? '—'}</span>,
            previsao: previsao(o),
          }))}
        />
      </div>

      <ul className="c360__lista c360__so-cel">
        {lista.itens.map((o) => (
          <ListaLinha
            key={o.referencia_externa}
            densa
            nome={o.titulo ?? 'Sem título'}
            {...(o.id ? { aoAbrir: () => aoAbrir(o.id!) } : {})}
            meta={
              <span className="c360__lista-meta">
                {o.etapa && <Chip tom="etapa">{nomeDaEtapa(o.etapa_nome ?? o.etapa)}</Chip>}
                <span>previsão {previsao(o)}</span>
              </span>
            }
            direita={<span className="c360__valor">{dinheiro(o.valor) ?? '—'}</span>}
          />
        ))}
      </ul>
    </>
  );
}
